/* ============================================================================
   Live updates: what a write changed, and for whom.
   ----------------------------------------------------------------------------
   Every user has a hub of their own (worker/live.ts) holding their open tabs.
   After a write succeeds, the route names who should hear about it and which
   topics changed; each of those users' tabs refetches what it shows of those
   topics. A personal write reaches only its author's other tabs. A board
   write reaches every member of the board.

   The message carries ids, never content (COPL-151). A board event can say
   which board, the board's version after the write, and which tasks'
   rows changed and who is on them, so a tab refetches those tasks instead
   of the whole board and the box can tell a change none of its agents is
   on. Everyone hearing a board event is on that board's audience
   (boardAudience), so the ids tell them nothing they could not read. What
   changed is still read through the routes and their access checks.

   Every field past `topics` and `tab` is optional, and an event without
   them means what it always did: anything under those topics may have
   changed. Old daemons ignore what they don't know.

   Shared by both sides so the Worker's topics and the client's topic → query
   map cannot disagree about the names.
   ========================================================================== */

export type LiveTopic =
  /** Which boards someone is on, and their names and counts. */
  | "boards"
  /** Anything on a board: tasks, stages, labels, members, comments. */
  | "board"
  | "settings"
  | "vault"
  | "notes"
  /** Calendar connections and events (your own only). */
  | "calendar"
  /** Someone's handle or picture: theirs, and everywhere it is shown. */
  | "people"
  /** Admin screens: users and invites. */
  | "admin"
  /** My API tokens and connected apps (settings). */
  | "tokens"
  /** My agents: their settings, boards, grants and tokens. */
  | "agents"
  /** My inbox: assigned to me, mentioned me. */
  | "inbox";

/**
 * What one write to a board changed, as its route knows it. Only `board` is
 * required; leaving out `tasks` says anything on the board may have changed
 * (stages, labels, members, docs), and a listener reads the board whole.
 */
export interface BoardChange {
  board: string;
  /** boards.version after the write, when the write bumped it in its own batch. */
  version?: number;
  /**
   * The tasks whose rows changed: the one written and the parents that moved
   * with it. A delete names the task it deleted (whose read is then a 404),
   * the children it unparented, the tasks that waited on it and everything
   * above it (COPL-210).
   */
  tasks?: string[];
  /** Everyone assigned to those tasks, before the write and after it. */
  assignees?: string[];
  /**
   * Which open tasks share changed files may have moved (task.overlap, the
   * overlap reads). With `tasks: []` it is the only thing that did: a
   * changed-files report (COPL-207), which changes no task's row and bumps
   * no version. A tab holding the board reads its overlap
   * (GET /api/boards/:id/overlap) instead of the board, and the box, which
   * shows no overlap, skips it as it skips any event naming none of its
   * agents' tasks.
   */
  overlap?: boolean;
}

export interface LiveEvent extends Partial<BoardChange> {
  topics: LiveTopic[];
  /** The tab that made the change, which has already refreshed itself. */
  tab: string | null;
}

/** An event that moves only overlap: no task's row, no version (BoardChange.overlap). */
export const onlyOverlap = (e: BoardChange) => e.overlap === true && e.tasks?.length === 0 && e.version === undefined;

/** More tasks than this in one event, and a tab reads the board whole rather than each task. */
export const PATCH_LIMIT = 10;

/** What a tab does about the board events it gathered for one board (planBoard). */
export type BoardPlan =
  /** Every event is already in the board it holds. */
  | { kind: "none" }
  /** Refetch these tasks, patch them in, and hold the board at `version`. */
  | { kind: "patch"; tasks: string[]; version: number }
  /** It can't tell from the events which tasks changed: ask the board what changed since its version (BoardChanges). */
  | { kind: "behind" }
  /** Something beyond its tasks changed (stages, labels, members, docs): read the board whole. */
  | { kind: "whole" };

/**
 * What a tab holding a board at version `held` (undefined: it holds none
 * that says) does about the events it gathered for that board. Events at or
 * below `held` are already in what it holds. The rest are patched in only
 * when they run on from `held` without a gap (held + 1, held + 2, ...: one
 * event each, nothing missed), each names its tasks, and together they name
 * at most PATCH_LIMIT. An event with a version and no tasks changed more
 * than tasks, and reads the board whole. Anything else (a missed or doubled
 * version, an event without a version, too many tasks) is behind: the tab
 * asks what changed since `held` (GET /api/boards/:id/changes, COPL-210).
 * An event that moves only overlap (onlyOverlap) is no task's, and left to
 * the caller.
 */
export function planBoard(held: number | undefined, events: BoardChange[]): BoardPlan {
  const fresh = events.filter((e) => !onlyOverlap(e) && (held === undefined || e.version === undefined || e.version > held));
  if (fresh.length === 0) return { kind: "none" };
  if (held === undefined || fresh.some((e) => e.version !== undefined && !e.tasks)) return { kind: "whole" };
  if (fresh.some((e) => e.version === undefined)) return { kind: "behind" };
  const versions = fresh.map((e) => e.version as number).sort((a, b) => a - b);
  if (versions.some((v, i) => v !== held + 1 + i)) return { kind: "behind" };
  const tasks = [...new Set(fresh.flatMap((e) => e.tasks ?? []))];
  if (tasks.length > PATCH_LIMIT) return { kind: "behind" };
  return { kind: "patch", tasks, version: versions[versions.length - 1] };
}

/**
 * How many of a board's versions its change log keeps (board_changes,
 * COPL-210): a tab further behind than this reads the board whole.
 */
export const CHANGES_KEPT = 500;

/** More tasks than this changed since a tab's version, and GET /api/boards/:id/changes tells it to read the board whole. */
export const CHANGES_LIMIT = 100;

/** The request header a tab identifies itself with. */
export const TAB_HEADER = "x-copland-tab";

/**
 * How long a daemon's socket counts as connected after the hub last heard
 * from it: the daemon pings every 30 s and gives a connection up after 75 s
 * of silence (PING_EVERY and SILENCE in daemon/core/src/live.rs), so the
 * hub gives it up at the same mark.
 */
export const LISTENING_SILENCE_MS = 75_000;

/** One open socket in a hub, as listeningSockets weighs it. */
export interface HeldSocket {
  /** The API token it was opened with; absent for a browser tab. */
  tokenId?: string;
  /** The host its daemon named when it opened it (domain/hosts.ts HOST_HEADER); absent when it named none. */
  host?: string;
  /** When it was opened (ms), or 0 when the hub doesn't know. */
  openedAt: number;
  /** When its last ping was answered (ms), or null when none has been. */
  pongAt: number | null;
}

/** A token heard from lately, and the host it is on (null: its socket named none). */
export interface Listening {
  tokenId: string;
  host: string | null;
}

/**
 * The tokens with a socket heard from within LISTENING_SILENCE_MS: opened,
 * or a ping answered, that recently, each with the host it named, once per
 * token and host. A browser tab has no token and never counts. A socket
 * whose peer died without a close can sit in the hub for a while, so being
 * held is not enough; a recent ping is the daemon still there.
 */
export function listeningSockets(sockets: HeldSocket[], now: number): Listening[] {
  const live = new Map<string, Listening>();
  for (const s of sockets) {
    if (!s.tokenId) continue;
    if (now - Math.max(s.openedAt, s.pongAt ?? 0) > LISTENING_SILENCE_MS) continue;
    const host = s.host ?? null;
    live.set(JSON.stringify([s.tokenId, host]), { tokenId: s.tokenId, host });
  }
  return [...live.values()];
}
